import type { AppTranslations } from '../i18n/app'

type TextImportStrings = AppTranslations['textImport']

/**
 * The message shown when POST /text-import/extract/ (pasted text or a URL)
 * fails.
 *
 * Chosen by the error envelope's `code`, never from its `detail` text, which
 * is English and written for developers — the same rule as
 * fileUploadErrorMessage. The URL codes come from url_extractor.URL_ERRORS
 * and youtube_extractor._YOUTUBE_ERRORS; both extractors share TOO_SHORT,
 * FETCH_FAILED and RATE_LIMITED, so those messages fit either. An unknown
 * code, or an error with no JSON body, falls back to a generic message; no
 * response at all is a network failure.
 */
export function textExtractErrorMessage(err: unknown, ti: TextImportStrings): string {
  const response = (err as { response?: { status?: number; data?: { detail?: unknown } } } | null)?.response
  if (!response) return ti.uploadErrNetwork

  const detail = response.data?.detail
  const code =
    detail && typeof detail === 'object' && typeof (detail as { code?: unknown }).code === 'string'
      ? (detail as { code: string }).code
      : null

  switch (code) {
    case 'BLOCKED_URL':
      return ti.extractErrBlockedUrl
    case 'TOO_LARGE':
      return ti.extractErrPageTooLarge
    case 'TIMEOUT':
      return ti.extractErrTimeout
    case 'NOT_HTML':
      return ti.extractErrNotHtml
    case 'PAYWALL':
      return ti.extractErrPaywall
    case 'FETCH_FAILED':
      return ti.extractErrFetchFailed
    case 'TOO_SHORT':
      return ti.extractErrTooShort
    case 'REDIRECT_LOOP':
      return ti.extractErrRedirectLoop
    case 'UNAVAILABLE':
      return ti.extractErrVideoUnavailable
    case 'NO_SUBTITLES':
      return ti.extractErrNoSubtitles
    case 'NO_LANGUAGE':
      return ti.extractErrNoSubtitleLanguage
    case 'RATE_LIMITED':
      return ti.extractErrRateLimited
    case 'NLP_MODEL_UNAVAILABLE':
    case 'SERVICE_UNAVAILABLE':
      return ti.extractErrUnavailable
  }
  return ti.extractErrGeneric
}
